/** 재원기간(n년 n개월 n일) 산출. 등록일(createdAt) 기준 경과 캘린더 차이. */
export interface Tenure {
  years: number
  months: number
  days: number
}

export interface TenureUnitLabels {
  years: string
  months: string
  days: string
}

/** y년 m월(0-base)에 d일을 두되, 해당 월에 count개월을 더하고 일은 그 달 말일로 클램프한 UTC 타임스탬프. */
function addUtcMonthsClamped(y: number, m: number, d: number, count: number): number {
  const total = m + count
  const ny = y + Math.floor(total / 12)
  const nm = ((total % 12) + 12) % 12
  const lastDay = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate() // 다음 달 0일 = 이번 달 말일
  return Date.UTC(ny, nm, Math.min(d, lastDay))
}

/**
 * `fromISO`(등록일/시작일, ISO 문자열)부터 `now`까지의 캘린더 차이를 {years, months, days}로 반환.
 * - 날짜 계산은 UTC 기준(러너/사용자 타임존과 무관하게 결정적).
 * - 미래 날짜/빈값/잘못된 값은 null.
 * - 월말(29~31일) 등록 + 다음 달이 짧은 경우 클램핑(예: 1/31+1개월=2월 말일) — date-fns intervalToDuration 규약과 일치.
 *   anchor-walk: from에 더할 수 있는 최대 개월수를 구한 뒤 남은 일수를 센다(일/개월 음수 불가).
 */
export function computeTenure(fromISO: string | null | undefined, now: Date = new Date()): Tenure | null {
  if (!fromISO) return null
  const from = new Date(fromISO)
  if (Number.isNaN(from.getTime())) return null

  const fy = from.getUTCFullYear()
  const fm = from.getUTCMonth()
  const fd = from.getUTCDate()
  const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const fromUTC = Date.UTC(fy, fm, fd)
  if (fromUTC > nowUTC) return null

  // from에 더해도 now를 넘지 않는 최대 개월수
  let totalMonths = (now.getUTCFullYear() - fy) * 12 + (now.getUTCMonth() - fm)
  if (addUtcMonthsClamped(fy, fm, fd, totalMonths) > nowUTC) totalMonths -= 1

  const anchorUTC = addUtcMonthsClamped(fy, fm, fd, totalMonths)
  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
    days: Math.round((nowUTC - anchorUTC) / 86_400_000),
  }
}

/** 앞쪽 0 단위는 숨기고, 표시가 시작된 뒤의 단위는 유지한다. */
export function formatTenure(tenure: Tenure, labels: TenureUnitLabels): string {
  const units = [
    { value: tenure.years, label: labels.years },
    { value: tenure.months, label: labels.months },
    { value: tenure.days, label: labels.days },
  ]
  const firstVisibleIndex = units.findIndex((unit) => unit.value > 0)
  const visibleUnits = units.slice(firstVisibleIndex === -1 ? units.length - 1 : firstVisibleIndex)
  return visibleUnits.map((unit) => `${unit.value}${unit.label}`).join(' ')
}
